// Devanagari helpers, ported one-to-one from tools/generate_paradigms.py.
// Strings are compared NFC-normalised; Devanagari letters are single UTF-16 code units,
// so slicing by index matches Python's code-point slicing for every stem we use.

import type { StemClass } from '@/prakrit/lib/grammar/types'

export const VIRAMA = '्'
export const ANUSVARA = 'ं'

export const MATRA: Record<string, string> = {
    'आ': 'ा',
    'इ': 'ि',
    'ई': 'ी',
    'उ': 'ु',
    'ऊ': 'ू',
    'ए': 'े',
    'ओ': 'ो'
}

export const LETTER_OF: Record<string, string> = Object.fromEntries(
    Object.entries(MATRA).map(([letter, sign]) => [sign, letter])
)

const LONG: Record<string, string> = { 'ि': 'ी', 'ु': 'ू', 'इ': 'ई', 'उ': 'ऊ' }
const SHORT: Record<string, string> = { 'ी': 'ि', 'ू': 'ु', 'ई': 'इ', 'ऊ': 'उ' }

const FINAL_VOWEL: Record<string, StemClass> = {
    'ा': 'ā',
    'आ': 'ā',
    'ि': 'i',
    'इ': 'i',
    'ी': 'ī',
    'ई': 'ī',
    'ु': 'u',
    'उ': 'u',
    'ू': 'ū',
    'ऊ': 'ū',
    'े': 'e',
    'ए': 'e',
    'ो': 'o',
    'ओ': 'o'
}

export const nfc = (value: string): string => value.normalize('NFC')

const last = (value: string): string => value.slice(-1)

/** The stem's final sound: a, ā, i, ī, u, ū, e or o. A bare consonant carries the inherent अ. */
export const finalVowel = (stem: string): StemClass => {
    const c = last(stem)

    if (c === 'अ') return 'a'

    return FINAL_VOWEL[c] ?? 'a'
}

/** Replace a final (inherent or written) अ with another vowel: पुरिस → पुरिसे, बालअ → बालए. */
export const setA = (stem: string, vowelLetter: string): string => {
    if (stem.endsWith('अ')) return stem.slice(0, -1) + vowelLetter

    const sign = MATRA[vowelLetter]

    if (sign === undefined) throw new Error(`No vowel sign for ${vowelLetter}`)

    return stem + sign
}

export const lengthen = (stem: string): string => {
    if (finalVowel(stem) === 'a') return setA(stem, 'आ')

    const c = last(stem)

    if (c in LONG) return stem.slice(0, -1) + LONG[c]

    return stem
}

export const shorten = (stem: string): string => {
    if (finalVowel(stem) === 'ā') return stem.slice(0, -1) + (stem.endsWith('आ') ? 'अ' : '')

    const c = last(stem)

    if (c in SHORT) return stem.slice(0, -1) + SHORT[c]

    return stem
}

/** Append a vowel to an a-stem, replacing the inherent/written अ: पढ + इ → पढि. */
export const addMatra = (stem: string, letter: string): string => setA(stem, letter)

// ---------------------------------------------------------------- text helpers

const PUNCTUATION = /[।॥?!,;:.“”"'‘’()[\]{}…—–\-]/g

/** Normalise one token for lookups: NFC, no punctuation, no zero-width joiners. */
export const cleanToken = (token: string): string => nfc(token.replace(PUNCTUATION, '').replace(/[​-‍﻿]/g, '').trim())

/** Split a Prakrit sentence into word tokens (punctuation removed). */
export const tokenize = (sentence: string): string[] => nfc(sentence)
    .split(/\s+/)
    .map(cleanToken)
    .filter(Boolean)

/**
 * Split text into user-perceived characters. Conjuncts (क्ख, ण्ह, स्स …) always stay together,
 * even on engines whose segmenter splits after a virama.
 */
export const graphemes = (text: string): string[] => {
    const value = nfc(text)
    let parts: string[]

    if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
        const segmenter = new Intl.Segmenter('hi', { granularity: 'grapheme' })

        parts = Array.from(segmenter.segment(value), segment => segment.segment)
    }
    else {
        parts = Array.from(value)
    }

    const merged: string[] = []

    for (const part of parts) {
        const previous = merged[merged.length - 1]

        if (previous !== undefined && previous.endsWith(VIRAMA) && /[क-हक़-य़]/.test(part[0] ?? '')) {
            merged[merged.length - 1] = previous + part
        }
        else {
            merged.push(part)
        }
    }

    return merged
}

/** Longest common prefix by grapheme-safe code units. */
export const commonPrefix = (a: string, b: string): string => {
    let i = 0

    while (i < a.length && i < b.length && a[i] === b[i]) i += 1

    return a.slice(0, i)
}

/** Turn a dependent vowel sign at the start of an ending into its independent letter for display. */
export const displayEnding = (ending: string): string => {
    const first = ending[0]

    if (first !== undefined && first in LETTER_OF) return LETTER_OF[first] + ending.slice(1)

    return ending
}

export const isDevanagari = (value: string): boolean => /[ऀ-ॿ]/.test(value)
